import { httpsCallable } from 'firebase/functions';
import { functions } from '../../services/firebase';
import { rememberCareSourceActivityFromResult } from './careSourceSessionActivity';
import { normalizeCareSourceType } from './sourceTypes';

const captureCareSourceNoteCallable = httpsCallable(
  functions,
  'captureCareSourceNote',
  { timeout: 30000 }
);

const organizeCapturedCareSourceNoteCallable = httpsCallable(
  functions,
  'organizeCapturedCareSourceNote',
  { timeout: 180000 }
);

const requestCareSourceReorganizationCallable = httpsCallable(
  functions,
  'requestCareSourceReorganization',
  { timeout: 60000 }
);

const applyCareSourceReorganizationProposalCallable = httpsCallable(
  functions,
  'applyCareSourceReorganizationProposal',
  { timeout: 60000 }
);

const finalizeImportHistoryReviewCallable = httpsCallable(
  functions,
  'finalizeImportHistoryReview',
  { timeout: 60000 }
);

const saveImportHistoryReviewDraftCallable = httpsCallable(
  functions,
  'saveImportHistoryReviewDraft',
  { timeout: 60000 }
);

const unwrapCallableResponse = (response) => (
  response?.data && typeof response.data === 'object' ? response.data : response
);

export const captureCareSourceForProcessing = async ({
  childId = '',
  rawText = '',
  sourceType = 'unknown',
  clientRequestId = '',
  sourceMetadata = {},
  deps = {},
} = {}) => {
  const callable = deps.captureCareSourceNote || captureCareSourceNoteCallable;
  const response = await callable({
    childId,
    rawText,
    sourceType: normalizeCareSourceType(sourceType),
    ...(clientRequestId ? { clientRequestId } : {}),
    sourceMetadata,
  });

  const result = unwrapCallableResponse(response);
  rememberCareSourceActivityFromResult(result);

  return result;
};

export const organizeCapturedCareSource = async ({
  sourceNoteId = '',
  options = {},
  deps = {},
} = {}) => {
  const callable = deps.organizeCapturedCareSourceNote || organizeCapturedCareSourceNoteCallable;
  const response = await callable({
    sourceNoteId,
    options,
  });

  return unwrapCallableResponse(response);
};

export const requestCareSourceReorganization = async ({
  sourceNoteId = '',
  organizedDraft = '',
  correction = '',
  deps = {},
} = {}) => {
  const callable = deps.requestCareSourceReorganization || requestCareSourceReorganizationCallable;
  const response = await callable({ sourceNoteId, organizedDraft, correction });
  return unwrapCallableResponse(response);
};

export const applyCareSourceReorganizationProposal = async ({
  sourceNoteId = '',
  proposalId = '',
  selectedChanges = [],
  deps = {},
} = {}) => {
  const callable = deps.applyCareSourceReorganizationProposal
    || applyCareSourceReorganizationProposalCallable;
  const response = await callable({ sourceNoteId, proposalId, selectedChanges });
  return unwrapCallableResponse(response);
};

export const finalizeImportHistoryReview = async ({
  sourceNoteId = '',
  decisions = [],
  deps = {},
} = {}) => {
  const callable = deps.finalizeImportHistoryReview || finalizeImportHistoryReviewCallable;
  const response = await callable({ sourceNoteId, decisions });
  return unwrapCallableResponse(response);
};

export const saveImportHistoryReviewDraft = async ({
  sourceNoteId = '',
  decisions = [],
  reviewed = false,
  deps = {},
} = {}) => {
  const callable = deps.saveImportHistoryReviewDraft || saveImportHistoryReviewDraftCallable;
  const response = await callable({ sourceNoteId, decisions, reviewed });
  return unwrapCallableResponse(response);
};
